/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import type { LinkProps } from "@packages/framework";
import { Link } from "@packages/framework";
import { handleOpenCookieManager, LINKS } from "@packages/sdk";
import type { HTMLAttributes } from "react";

import {
  radius,
  spacing,
  stroke,
  type ThemeType,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { Text } from "../Text/Text.emotion";

const aStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  borderColor: theme.semanticColors.neutralsLower,
  borderRadius: radius.full,
  borderStyle: "solid",
  borderWidth: stroke.light,
  color: "inherit",
  cursor: "pointer",
  paddingBottom: spacing.xs,
  paddingTop: spacing.xs,
  textAlign: "center",
  [cssMediaQueryMinWidthTablet]: {
    borderWidth: stroke.none,
    paddingBottom: spacing.none,
    paddingTop: spacing.none,
    textAlign: "start",
  },
});
const divStyle: Interpolation = {
  alignItems: "flex-start",
  display: "grid",
  gap: spacing.s,
  gridTemplateColumns: "repeat(2, 1fr)",
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
    display: "flex",
    gap: spacing.l,
    width: "fit-content",
  },
};
const footerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "flex-start",
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.neutralsMedLow,
  display: "flex",
  flexDirection: "column-reverse",
  gap: spacing.m,
  justifyContent: "flex-start",
  paddingBottom: spacing.l,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  paddingTop: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.none,
    justifyContent: "space-between",
    paddingBottom: spacing.xl,
    paddingTop: spacing.xl,
  },
});

type MinimalFooterProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  links?: {
    message: string;
    props: LinkProps;
  }[];
};

export const MinimalFooter = ({
  links = [
    LINKS.web_copyright,
    LINKS.web_privacy_policy,
    LINKS.web_terms_of_service,
  ],
  ...props
}: MinimalFooterProps) => (
  <Text asChild size="xl" type="detail">
    <footer css={footerStyle} {...props}>
      <span>© {new Date().getFullYear()} Hallow, Inc.</span>
      <div css={divStyle}>
        <button onClick={handleOpenCookieManager} css={aStyle}>
          <I18nMessage message="web_manage_cookies" />
        </button>
        {links.map(({ message, props: linkProps }) => (
          <Link key={message} css={aStyle} prefetch={false} {...linkProps}>
            <I18nMessage message={message} />
          </Link>
        ))}
      </div>
    </footer>
  </Text>
);

MinimalFooter.displayName = "MinimalFooter";
